import React, { createContext, useContext, useState, useEffect } from 'react';
import { formatErrorMessage } from '../../utils/errorHandler';
import logger from '../../utils/logger';
import i18n from '../../i18n';
import { getPreviewToken, isPreviewMode } from '../../lib/previewAuth';
import { isMobileAdminAppView } from '../../lib/appView';

const AuthContext = createContext();

// Applique la préférence de langue enregistrée en base pour l'utilisateur (fr | en).
// N'écrase pas la langue si l'utilisateur n'a pas encore de préférence (language null).
const applyUserLanguage = (userData) => {
  const lng = (userData?.language || '').toLowerCase().split('-')[0];
  if (lng === 'fr' || lng === 'en') {
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [mustAcceptCgu, setMustAcceptCgu] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Vérifier le statut du mot de passe au chargement
  const checkPasswordStatus = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/check-password-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        logger.debug('📋 Password status check:', data);
        // Pour les collaborateurs OU les admins
        const shouldChangePassword = data.must_change_password && (data.is_admin || data.is_collaborator);
        setMustChangePassword(shouldChangePassword);
        setMustAcceptCgu(data.must_accept_cgu || false);
      } else {
        logger.error('❌ Error checking password status:', response.status);
        setMustChangePassword(false);
        setMustAcceptCgu(false);
      }
    } catch (error) {
      logger.error('❌ Error checking password status:', error);
      setMustChangePassword(false);
      setMustAcceptCgu(false);
    }
  };

  // Check if user is logged in on app start
  useEffect(() => {
    // Priorité: token de preview (iframe dev) > impersonation (sessionStorage) > session normale
    const previewToken = getPreviewToken();
    const impersonateToken = sessionStorage.getItem('impersonate_token');
    const normalToken = localStorage.getItem('token');
    const token = previewToken || impersonateToken || normalToken;
    
    if (token) {
      fetchUserProfile(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();

        // Session timeout (admins) : au retour après une inactivité prolongée
        // (onglet fermé), on ne ressuscite pas la session via le refresh token.
        // Désactivé sur la web app mobile admin (sécurité de l'appareil suffisante).
        const isAdminRole = userData?.role === 'admin' || userData?.role === 'super_admin';
        const isNormalSession = !getPreviewToken() && !sessionStorage.getItem('impersonate_token');
        if (isAdminRole && isNormalSession && !isMobileAdminAppView()) {
          const lastRaw = localStorage.getItem('pitboard_last_activity');
          const timeoutMs = (userData.inactivity_timeout || 30) * 60 * 1000;
          if (lastRaw && (Date.now() - Number(lastRaw)) > timeoutMs) {
            logger.debug('⏱️ Session expirée par inactivité — déconnexion automatique');
            await logout();
            return;
          }
        }
        try { localStorage.setItem('pitboard_last_activity', String(Date.now())); } catch { /* ignore */ }

        setUser(userData);
        applyUserLanguage(userData);
        
        // Vérifier si changement de mot de passe requis
        await checkPasswordStatus(token);
      } else {
        // Token is invalid, remove it (SAUF en mode preview : le storage est partagé
        // avec la fenêtre parente admin, il ne faut pas casser sa session).
        if (!isPreviewMode()) localStorage.removeItem('token');
        setUser(null);
      }
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      if (!isPreviewMode()) localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });

      const data = await response.json();

      if (response.ok) {
        // Vérifier si 2FA est requis
        if (data.requires_2fa) {
          logger.debug('🔍 2FA requis détecté dans AuthContext');
          return {
            requires_2fa: true,
            temp_token: data.temp_token,
            user_id: data.user_id,
            message: data.message
          };
        }
        
        // Connexion normale sans 2FA
        const { access_token, user: userData } = data;
        localStorage.setItem('token', access_token);
        setUser(userData);
        applyUserLanguage(userData);
        
        // Vérifier si changement de mot de passe requis après connexion
        await checkPasswordStatus(access_token);
        
        return { success: true, user: userData };
      } else {
        const errorMessage = formatErrorMessage(data, 'Erreur de connexion');
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      logger.error('Login error:', error);
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, user: data };
      } else {
        const errorMessage = formatErrorMessage(data, 'Erreur lors de l\'inscription');
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      logger.error('Register error:', error);
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  };

  const logout = async () => {
    // S4 : invalider le cookie refresh côté serveur (sinon la session pourrait être ressuscitée)
    try {
      const rawFetch = (window.fetch && window.fetch.__originalFetch) || window.fetch;
      await rawFetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch {
      // Échec réseau : on déconnecte quand même côté client
    }
    localStorage.removeItem('token');
    localStorage.removeItem('pitboard_last_activity');
    sessionStorage.removeItem('impersonate_token');
    setUser(null);
    setMustChangePassword(false);
    setMustAcceptCgu(false);
    window.location.href = '/';
  };

  const onPasswordChanged = () => {
    setMustChangePassword(false);
  };

  const onCguAccepted = () => {
    setMustAcceptCgu(false);
  };

  const acceptCgu = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/accept-cgu`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMustAcceptCgu(false);
        return { success: true };
      } else {
        return { success: false, error: 'Erreur lors de l\'acceptation des CGU' };
      }
    } catch (error) {
      logger.error('Error accepting CGU:', error);
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  };

  // Permission checks
  const hasRole = (role) => {
    return user && user.role === role;
  };

  const hasAnyRole = (roles) => {
    return user && roles.includes(user.role);
  };

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'super_admin');
  };

  const isSuperAdmin = () => {
    return user && user.role === 'super_admin';
  };

  const canAccessSpace = (spaceType) => {
    if (!user) return false;
    
    // Super admin peut accéder à tout
    if (user.role === 'super_admin') return true;
    
    // Autres rôles selon leur espace
    switch (spaceType) {
      case 'admin':
        return user.role === 'admin';
      case 'client':
        return user.role === 'client';
      case 'partner':
        return user.role === 'partner';
      case 'brokoli':
        return user.role === 'brokoli';
      case 'assisteur':
        return user.role === 'assisteur';
      case 'assistance_client':
        return user.role === 'assistance_client';
      default:
        return false;
    }
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await fetchUserProfile(token);
    }
  };

  // Récupérer le token actif (preview > impersonation > normal)
  const getAuthToken = () => {
    return getPreviewToken() || sessionStorage.getItem('impersonate_token') || localStorage.getItem('token');
  };

  const value = {
    user,
    loading,
    mustChangePassword,
    mustAcceptCgu,
    login,
    register,
    logout,
    onPasswordChanged,
    onCguAccepted,
    acceptCgu,
    refreshUser,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
    canAccessSpace,
    isAuthenticated: !!user,
    isClient: user?.role === 'client',
    isPartner: user?.role === 'partner',
    isCollaborator: user?.is_collaborator || false,
    authToken: getAuthToken()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
